import { useCallback, useEffect, useState } from 'react';
import { logger } from '../utils/logger';

/**
 * A hook that syncs state with localStorage.
 * Supports SSR by initializing with the default value and hydrating on mount.
 *
 * @param key The localStorage key to use
 * @param defaultValue The default value if nothing is in localStorage
 * @returns A stateful value and a function to update it (persists to localStorage)
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Initialize with value from localStorage if available (synchronous read)
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      if (typeof window === 'undefined') {
        return defaultValue;
      }
      const item = localStorage.getItem(key);
      logger.debug(
        `[useLocalStorage] Initial load for "${key}":`,
        item ? JSON.parse(item) : defaultValue
      );
      return item !== null ? JSON.parse(item) : defaultValue;
    } catch (error) {
      logger.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  // No longer needed - we load synchronously in useState initializer
  // This useEffect is kept for potential future cross-tab synchronization
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch (error) {
          logger.warn(`Error parsing storage event for key "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  // Memoized setter that also persists to localStorage
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue((prev) => {
        const valueToStore = value instanceof Function ? value(prev) : value;
        logger.debug(`[useLocalStorage] Setting "${key}":`, valueToStore);
        try {
          localStorage.setItem(key, JSON.stringify(valueToStore));
          logger.debug(`[useLocalStorage] Successfully saved to localStorage`);
        } catch (error) {
          logger.warn(`Error setting localStorage key "${key}":`, error);
        }
        return valueToStore;
      });
    },
    [key]
  );

  return [storedValue, setValue];
}
